import { Game } from './game';
import { CONFIG } from './config';
import { Render } from './render';
import { Audio2 } from './audio';
import { Input } from './input';
import { Meta } from './meta';
import { rand, clamp, TAU, weaponDef, fmtTime } from './utils';
import { t, tName, tDesc, tHint, Locale, LANGS, LANG_LABELS } from './locale';

/* =====================================================================
   UI — весь рендер: мир, HUD, оверлеи (меню / драфт / пауза / итоги).
   ===================================================================== */

export const UI: any = {
  // 2D-ОВЕРЛЕЙ (поверх pixi-канваса с миром+тьмой). Рисует только текст/бары/меню;
  // мир, тьму и глинты рисует PixiRenderer снизу. Холст очищается прозрачно, чтобы
  // pixi-мир просвечивал; для меню-экранов кладём собственный фон.
  // Тряска (Game.shakeX/Y) считается здесь через seeded rand (часть детерминизма) —
  // pixi-рендер мира её ЧИТАЕТ. Поэтому UI.render зовётся ДО PixiRenderer.render().
  render(ctx) {
    const W = Game.viewW, H = Game.viewH;
    // вычислить тряску один раз за кадр
    if (Game.shake > 0) {
      Game.shakeX = rand(-Game.shake, Game.shake);
      Game.shakeY = rand(-Game.shake, Game.shake);
    } else { Game.shakeX = 0; Game.shakeY = 0; }

    ctx.clearRect(0, 0, W, H);

    if (Game.state === 'menu') { this.drawBg(ctx); this.drawMenu(ctx); return; }
    if (Game.state === 'charselect') { this.drawBg(ctx); this.drawCharSelect(ctx); return; }
    if (Game.state === 'shop') { this.drawBg(ctx); this.drawShop(ctx); return; }
    if (Game.state === 'settings') { this.drawSettings(ctx); return; }

    this.drawWorldUI(ctx);   // числа урона, HP-бары танков/боссов (мировое простр.)
    this.drawHUD(ctx);
    this.drawBanner(ctx);
    if (Game.state === 'playing') this.drawJoystick(ctx);

    if (Game.state === 'playing') { this.drawShrineIndicators(ctx); this.drawBossIndicator(ctx); this.drawSnareIndicator(ctx); }
    if (Game.state === 'levelup' || Game.state === 'shrine') this.drawDraft(ctx);
    else if (Game.state === 'paused') this.drawPaused(ctx);
    else if (Game.state === 'speak') this.drawSpeak(ctx);
    else if (Game.state === 'gameover') this.drawGameOver(ctx);
    else if (Game.state === 'win') this.drawWin(ctx);
  },

  // ===================================================================
  // ГОРНИЛО — общие примитивы визуального языка (плита/фаска/скобки/жар-текст/
  // клеймо/вены/иконки). Все drawXxx рисуют ЧЕРЕЗ них → единый язык, не дефолт-боксы.
  // ===================================================================
  DISP: "Oswald, sans-serif",                  // дисплейный: заголовки / числа / клейма
  MONO: "ui-monospace, Consolas, monospace",   // плотный body: описания / тултипы
  // шрифт-строка: f(размер, начертание[, mono]). weight: 200(тонкий)…700(тяжёлый).
  f(size, weight?, mono?) { return (weight || 600) + ' ' + size + 'px ' + (mono ? this.MONO : this.DISP); },

  // кеш вертикальных градиентов плит: HUD-плиты рисуются каждый кадр (60fps) — не аллоцируем
  // новый градиент на кадр, ключ по (y,h,цвета). Зависит только от ctx (стабилен) и координат.
  _gradCache: {} as Record<string, any>,
  // кованая плита с направленной фаской: верх+лево ловят бело-горячий свет, низ+право обуглены.
  plate(ctx, x, y, w, h, o?) {
    o = o || {}; const c = CONFIG.colors;
    let fill = o.fill;
    if (!fill) {
      const f0 = o.fill0 || 'rgba(34,24,11,0.90)', f1 = o.fill1 || 'rgba(13,9,5,0.93)';
      const key = (y | 0) + '_' + (h | 0) + '_' + f0 + '_' + f1;
      fill = this._gradCache[key];
      if (!fill) {
        const g = ctx.createLinearGradient(0, y, 0, y + h);
        g.addColorStop(0, f0); g.addColorStop(1, f1);
        this._gradCache[key] = fill = g;
      }
    }
    ctx.fillStyle = fill; ctx.fillRect(x, y, w, h);
    if (o.veins) this.vein(ctx, x, y, w, h, o.veins === true ? 0.4 : o.veins);
    ctx.lineWidth = o.bevel || 1.5;
    ctx.strokeStyle = o.hi || c.plateHi;
    ctx.beginPath(); ctx.moveTo(x + 0.5, y + h); ctx.lineTo(x + 0.5, y + 0.5); ctx.lineTo(x + w, y + 0.5); ctx.stroke();
    ctx.strokeStyle = o.lo || c.plateLo;
    ctx.beginPath(); ctx.moveTo(x + w - 0.5, y); ctx.lineTo(x + w - 0.5, y + h - 0.5); ctx.lineTo(x, y + h - 0.5); ctx.stroke();
  },

  // ember-вены, пробивающиеся сквозь обугленную плиту (screen-blend, диагонали)
  vein(ctx, x, y, w, h, a = 0.4) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.globalCompositeOperation = 'screen'; ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,150,50,' + a.toFixed(2) + ')';
    ctx.beginPath();
    ctx.moveTo(x - 4, y + h * 0.72); ctx.lineTo(x + w * 0.4, y + h * 0.34); ctx.lineTo(x + w * 0.62, y + h * 0.5);
    ctx.moveTo(x + w * 0.18, y + h + 4); ctx.lineTo(x + w * 0.5, y + h * 0.6);
    ctx.stroke();
    ctx.restore();
  },

  // скобки-углы (клеймо активной рамки): по умолчанию верх-лево + низ-право; all=4 угла.
  bracket(ctx, x, y, w, h, color, len = 13, lw = 2, all = false) {
    ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.beginPath();
    ctx.moveTo(x, y + len); ctx.lineTo(x, y); ctx.lineTo(x + len, y);
    ctx.moveTo(x + w - len, y + h); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w, y + h - len);
    if (all) {
      ctx.moveTo(x + w, y + len); ctx.lineTo(x + w, y); ctx.lineTo(x + w - len, y);
      ctx.moveTo(x + len, y + h); ctx.lineTo(x, y + h); ctx.lineTo(x, y + h - len);
    }
    ctx.stroke();
  },

  // жар-текст: заголовок/число; temp = цвет (температура), glow = опц. ореол.
  heatText(ctx, text, x, y, size, weight, temp, align = 'center', glow?) {
    ctx.textAlign = align; ctx.font = this.f(size, weight);
    if (glow) { ctx.shadowColor = glow; ctx.shadowBlur = size * 0.55; }
    ctx.fillStyle = temp; ctx.fillText(text, x, y);
    if (glow) ctx.shadowBlur = 0;
  },

  // кнопка-клеймо. prime — залитый янтарь (главное действие); иначе ghost — плита с янтарной кромкой.
  // Возвращает rect для hit-теста. size — кегль подписи.
  stamp(ctx, x, y, w, h, label, o?) {
    o = o || {}; const c = CONFIG.colors; const hov = o.hover;
    const size = o.size || 17;
    const tx = o.icon ? x + 46 : (o.prime ? x + 18 : x + 16);
    const ty = y + h / 2 + size * 0.36;
    if (o.prime) {
      const g = ctx.createLinearGradient(0, y, 0, y + h);
      g.addColorStop(0, hov ? '#ffc24a' : c.ember); g.addColorStop(1, c.emberDim);
      ctx.fillStyle = g; ctx.fillRect(x, y, w, h);
      ctx.lineWidth = 2; ctx.strokeStyle = c.hot;
      ctx.beginPath(); ctx.moveTo(x, y + 1); ctx.lineTo(x + w, y + 1); ctx.stroke();
      ctx.strokeStyle = c.plateLo;
      ctx.beginPath(); ctx.moveTo(x, y + h - 1); ctx.lineTo(x + w, y + h - 1); ctx.stroke();
      if (o.icon) this.icon(ctx, o.icon, x + 26, y + h / 2, size + 2, c.char0);
      this.heatText(ctx, label, tx, ty, size, 600, c.char0, 'left');
    } else {
      this.plate(ctx, x, y, w, h, { fill0: hov ? 'rgba(40,28,12,0.92)' : 'rgba(24,17,9,0.85)', fill1: 'rgba(12,9,5,0.9)' });
      ctx.fillStyle = hov ? c.ember : c.emberDim; ctx.fillRect(x, y, 3, h);   // янтарная кромка слева — «приклёпана»
      const col = hov ? c.hot : (o.color || c.ember);
      if (o.icon) this.icon(ctx, o.icon, x + 24, y + h / 2, size, col);
      this.heatText(ctx, label, tx, ty, size, hov ? 600 : 500, col, 'left');
    }
    return { x, y, w, h };
  },

  // path-иконки чрома (заменяют unicode-глифы ICONS): чёткие, читаемы мелко, single-weight.
  // Контентные глифы (оружие/пассивки) пока на heat-диске (icon миграция CONFIG — следующий слой).
  icon(ctx, key, cx, cy, s, color) {
    ctx.save(); ctx.translate(cx, cy); const u = s / 2;
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = Math.max(1.5, s * 0.09);
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.beginPath();
    switch (key) {
      case 'play': ctx.moveTo(-u * 0.6, -u); ctx.lineTo(u, 0); ctx.lineTo(-u * 0.6, u); ctx.closePath(); ctx.fill(); break;
      case 'forge': // наковальня
        ctx.moveTo(-u, -u * 0.2); ctx.lineTo(u, -u * 0.2); ctx.lineTo(u * 0.5, u * 0.2); ctx.lineTo(-u * 0.3, u * 0.2); ctx.closePath();
        ctx.moveTo(-u * 0.15, u * 0.2); ctx.lineTo(-u * 0.15, u); ctx.moveTo(-u * 0.5, u); ctx.lineTo(u * 0.2, u); ctx.stroke(); break;
      case 'gear': {
        ctx.arc(0, 0, u * 0.5, 0, TAU); ctx.moveTo(0, -u); ctx.lineTo(0, -u * 0.6);
        for (let i = 0; i < 6; i++) { const a = i / 6 * TAU; ctx.moveTo(Math.cos(a) * u * 0.7, Math.sin(a) * u * 0.7); ctx.lineTo(Math.cos(a) * u, Math.sin(a) * u); }
        ctx.stroke(); break; }
      case 'reroll': ctx.arc(0, 0, u * 0.7, 0.5, TAU - 0.2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(u * 0.6, -u * 0.55); ctx.lineTo(u * 0.62, u * 0.1); ctx.lineTo(u * 1.05, -u * 0.2); ctx.closePath(); ctx.fill(); break;
      case 'skull': ctx.arc(0, -u * 0.1, u * 0.62, Math.PI, TAU); ctx.lineTo(u * 0.62, u * 0.35); ctx.lineTo(-u * 0.62, u * 0.35); ctx.closePath(); ctx.fillStyle = color; ctx.fill();
        ctx.fillStyle = '#0a0908'; ctx.beginPath(); ctx.arc(-u * 0.25, -u * 0.05, u * 0.16, 0, TAU); ctx.arc(u * 0.25, -u * 0.05, u * 0.16, 0, TAU); ctx.fill(); break;
      case 'coin': ctx.arc(0, 0, u * 0.85, 0, TAU); ctx.stroke(); ctx.beginPath(); ctx.moveTo(0, -u * 0.45); ctx.lineTo(0, u * 0.45); ctx.moveTo(-u * 0.3, -u * 0.15); ctx.lineTo(u * 0.3, -u * 0.15); ctx.stroke(); break;
      case 'lock': ctx.rect(-u * 0.6, -u * 0.05, u * 1.2, u * 0.95); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, -u * 0.05, u * 0.42, Math.PI, TAU); ctx.stroke(); break;
      case 'back': ctx.moveTo(u * 0.6, -u * 0.6); ctx.lineTo(-u * 0.4, 0); ctx.lineTo(u * 0.6, u * 0.6); ctx.stroke(); break;
      case 'warn': ctx.moveTo(0, -u); ctx.lineTo(u, u * 0.7); ctx.lineTo(-u, u * 0.7); ctx.closePath(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, -u * 0.3); ctx.lineTo(0, u * 0.2); ctx.moveTo(0, u * 0.45); ctx.lineTo(0, u * 0.5); ctx.stroke(); break;
      default: ctx.arc(0, 0, u * 0.6, 0, TAU); ctx.stroke();
    }
    ctx.restore();
  },

  // контентный глиф на «struck-диске» (медальон) — глиф читается как клеймо-на-токене,
  // согласовано с языком даже до полной миграции иконок оружия/пассивок в path'и.
  glyphToken(ctx, glyph, cx, cy, disc, color, glyphSize) {
    ctx.globalCompositeOperation = 'lighter';
    Render.blit(ctx, Render.glowDot(color, disc), cx, cy, 0, 1, 0.18);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = color; ctx.font = glyphSize + "px 'Oswald', serif"; ctx.textAlign = 'center';
    ctx.fillText(glyph, cx, cy + glyphSize * 0.34);
  },

  // Говорящая фигура у тайника/источника (§12): мир замер, фраза-голос над фигурой + подсказка.
  // Затемнение лёгкое (мир виден за фигурой), текст «всплывает» и держится — даёт ВЕС моменту.
  drawSpeak(ctx) {
    const sp = Game.speak; if (!sp) return;
    const W = Game.viewW, H = Game.viewH, p = Game.player;
    this.dim(ctx, 0.45);
    // экранная позиция фигуры (мир заморожен, камера статична)
    const fx = sp.x - p.x + W / 2 + Game.shakeX;
    const fy = sp.y - p.y + H / 2 + Game.shakeY;
    const age = sp.t, appear = Math.min(1, age / 0.4);                 // плавное появление текста
    const fade = sp.dur - age < 0.5 ? Math.max(0, (sp.dur - age) / 0.5) : 1;
    const a = appear * fade;
    ctx.textAlign = 'center';
    // фраза-голос над фигурой, в кавычках-ёлочках, тёплый пепельный курсив
    ctx.globalAlpha = a;
    ctx.fillStyle = '#d8c6b0';
    ctx.font = this.f(21, 400, true);
    const ty = Math.max(70, fy - 64) - (1 - appear) * 10;             // чуть «всплывает» снизу
    ctx.fillText('« ' + sp.voice + ' »', W / 2, ty);
    // подсказка «продолжить» — тускло, пульс
    ctx.globalAlpha = a * (0.4 + 0.3 * Math.sin((Game.clock || 0) * 3));
    this.heatText(ctx, t('speak.continue'), W / 2, H - 54, 13, 300, CONFIG.colors.ash, 'center');
    ctx.globalAlpha = 1;
  },

  // стрелки-индикаторы к Святилищам вне экрана (мотивация двигаться к свету вдали)
  drawShrineIndicators(ctx) {
    if (!Game.shrines || !Game.shrines.length) return;
    const W = Game.viewW, H = Game.viewH, p = Game.player;
    const cx = W / 2, cy = H / 2;
    for (const sh of Game.shrines) {
      const X = this.sx(sh.x), Y = this.sy(sh.y);
      const onScreen = X > 10 && X < W - 10 && Y > 10 && Y < H - 10;
      if (onScreen) continue;   // на экране — видно сам маяк, стрелка не нужна
      const ang = Math.atan2(sh.y - p.y, sh.x - p.x);
      const rx = (W / 2 - 48), ry = (H / 2 - 48);
      // позиция стрелки на эллипсе у края экрана
      const ix = cx + Math.cos(ang) * rx, iy = cy + Math.sin(ang) * ry;
      const dist = Math.round(Math.hypot(sh.x - p.x, sh.y - p.y) / 10);   // условные «шаги» до цели
      ctx.save();
      ctx.translate(ix, iy); ctx.rotate(ang);
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = sh.def.color;
      ctx.beginPath(); ctx.moveTo(12, 0); ctx.lineTo(-7, -7); ctx.lineTo(-7, 7); ctx.closePath(); ctx.fill();
      ctx.restore();
      // иконка + ПОДПИСЬ ТИПА (что там) + дистанция — стрелки больше не «непонятные»
      ctx.globalAlpha = 0.85; ctx.textAlign = 'center';
      ctx.fillStyle = sh.def.color; ctx.font = '15px serif';
      ctx.fillText(sh.def.icon, ix, iy - 16);
      ctx.font = 'bold 11px Consolas, monospace';
      ctx.fillText(tName('shrineKinds', sh.kind, sh.def.name) + '  ' + dist, ix, iy + 18);
      ctx.globalAlpha = 1;
    }
  },

  // стрелка-указатель к боссу вне экрана (жалоба плейтеста: «потерял жнеца»).
  // Босс теперь не теряется: его нельзя оторвать (резинка/телепорт) + видно куда он.
  drawBossIndicator(ctx) {
    const W = Game.viewW, H = Game.viewH, p = Game.player;
    const cx = W / 2, cy = H / 2;
    for (const e of Game.enemies.active) {
      if (e.dead || !e.isBoss) continue;
      const X = this.sx(e.x), Y = this.sy(e.y);
      if (X > 0 && X < W && Y > 0 && Y < H) continue;   // босс на экране — стрелка не нужна
      const ang = Math.atan2(e.y - p.y, e.x - p.x);
      const ix = cx + Math.cos(ang) * (W / 2 - 40), iy = cy + Math.sin(ang) * (H / 2 - 40);
      const pulse = 0.7 + 0.3 * Math.sin((Game.clock || 0) * 6);
      ctx.save();
      ctx.translate(ix, iy); ctx.rotate(ang);
      ctx.globalAlpha = pulse;
      ctx.fillStyle = CONFIG.colors.boss;
      ctx.beginPath(); ctx.moveTo(16, 0); ctx.lineTo(-9, -9); ctx.lineTo(-9, 9); ctx.closePath(); ctx.fill();
      ctx.restore();
      ctx.globalAlpha = pulse; ctx.textAlign = 'center';
      ctx.fillStyle = CONFIG.colors.boss; ctx.font = 'bold 14px Consolas, monospace';
      ctx.fillText('⚠ ' + tName('enemies', e.typeKey, CONFIG.enemies[e.typeKey].name), ix, iy - 16);
      ctx.globalAlpha = 1;
    }
  },

  // ТЕНЁТА (TD-захват): дисплей у точки в мире. kindle — циферблат РОЗЖИГА (оранжевый); defend — ЦЕЛОСТНОСТЬ
  // ядра (красный, тревожный). Внешняя дуга — остаток окна. Вне экрана — стрелка-указатель к точке с %.
  drawSnareIndicator(ctx) {
    if (!(Game.snareT > 0)) return;
    const W = Game.viewW, H = Game.viewH;
    const defend = Game.snareMode === 'defend';
    const frac = defend ? (Game.snareCoreMaxHp > 0 ? Game.snareCoreHp / Game.snareCoreMaxHp : 0)
                        : Math.max(0, Math.min(1, Game.snareKindle || 0));
    const arc = defend ? '255,90,60' : '255,150,80';   // defend — тревожный красный, kindle — тёплый оранжевый
    const pulse = 0.6 + 0.4 * Math.sin((Game.clock || 0) * 5);
    let cx = this.sx(Game.snareX), cy = this.sy(Game.snareY);
    const onScreen = cx > 40 && cx < W - 40 && cy > 40 && cy < H - 40;
    if (onScreen) {
      const R = 42;
      const tf = Math.max(0, Math.min(1, Game.snareT / CONFIG.snare.dur));   // доля окна — удержание ограничено
      ctx.save();
      ctx.lineWidth = 5; ctx.strokeStyle = 'rgba(199,91,106,0.22)';
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(' + arc + ',' + (0.55 + 0.3 * pulse).toFixed(2) + ')';
      ctx.beginPath(); ctx.arc(cx, cy, R, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac); ctx.stroke();
      // тонкая внешняя дуга — остаток окна (убывает), без текста (язык-агностично)
      ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(255,217,160,0.32)';
      ctx.beginPath(); ctx.arc(cx, cy, R + 7, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * tf); ctx.stroke();
      ctx.restore();
      ctx.textAlign = 'center'; ctx.fillStyle = defend ? '#ffb0a0' : '#ffd9a0'; ctx.font = this.f(18, 700);
      ctx.fillText(Math.round(frac * 100) + '%', cx, cy - R - 10);
      // СЛОЙ 1: HUD волны (только defend) — стадия читается как стадия; в паузе зовём следующую
      if (defend && Game.snareWave > 0) {
        ctx.fillStyle = '#ffe0b0'; ctx.font = this.f(14, 700);
        const wave = Game.snareWavePhase === 'gap' ? t('hud.snareNextWave') : t('hud.snareWave', { n: Game.snareWave, total: CONFIG.snare.defend.waves });
        ctx.fillText(wave, cx, cy - R - 28);
      }
      // ПРОТОТИП свет-маятника: подсказка постановки углей (только defend, пока есть бюджет)
      if (defend && Game.emberBudget > 0) {
        ctx.fillStyle = '#ffcf6e'; ctx.font = this.f(13, 700);
        ctx.fillText('E · угли ×' + Game.emberBudget, cx, cy + R + 18);
      }
    } else {
      // точка за кадром — стрелка от центра экрана в её сторону + % (зови игрока обратно)
      const mx = W / 2, my = H / 2, ang = Math.atan2(cy - my, cx - mx);
      const ex = mx + Math.cos(ang) * (Math.min(W, H) * 0.36);
      const ey = my + Math.sin(ang) * (Math.min(W, H) * 0.36);
      ctx.save();
      ctx.translate(ex, ey); ctx.rotate(ang);
      ctx.fillStyle = 'rgba(' + arc + ',' + (0.55 + 0.35 * pulse).toFixed(2) + ')';
      ctx.beginPath(); ctx.moveTo(14, 0); ctx.lineTo(-8, -9); ctx.lineTo(-8, 9); ctx.closePath(); ctx.fill();
      ctx.restore();
      ctx.textAlign = 'center'; ctx.fillStyle = defend ? '#ffb0a0' : '#ffd9a0'; ctx.font = this.f(15, 700);
      ctx.fillText(Math.round(frac * 100) + '%', ex, ey - 14);
    }
  },

  // непрозрачный фон под меню-экранами (в игре фон даёт pixi-канвас)
  drawBg(ctx) { ctx.fillStyle = CONFIG.colors.bg; ctx.fillRect(0, 0, Game.viewW, Game.viewH); },

  // 2D-слой над миром: числа урона + HP-бары танков/боссов (текст и тонкие бары
  // остаются на Canvas2D, чтобы не тащить PIXI.Text; затемнение тьмой для них опущено
  // — несущественно, они появляются у целей в освещённой зоне боя).
  drawWorldUI(ctx) {
    const p = Game.player;
    const lr2 = p.light * p.light;
    for (const e of Game.enemies.active) {
      if (e.dead) continue;
      if (e.isBoss) { this.drawBossBar(ctx, e); continue; }
      if (e.typeKey === 'tank' && e.hp < e.maxHp) {
        // HP-бар только в радиусе света — иначе он выдаёт позицию врага во тьме (жалоба плейтеста о визуальном шуме)
        const dx = e.x - p.x, dy = e.y - p.y;
        if (dx * dx + dy * dy > lr2) continue;
        const X = this.sx(e.x), Y = this.sy(e.y), w = e.radius * 2;
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(X - w / 2, Y - e.radius - 9, w, 4);
        ctx.fillStyle = CONFIG.colors.hp;
        ctx.fillRect(X - w / 2, Y - e.radius - 9, w * (e.hp / e.maxHp), 4);
      }
    }
    ctx.textAlign = 'center';
    ctx.lineJoin = 'round';
    for (const d of Game.dmgNumbers.active) {
      if (d.dead) continue;
      // числа урона во тьме не показываем — выдают позицию и засоряют (жалоба плейтеста)
      const ddx = d.x - p.x, ddy = d.y - p.y;
      if (ddx * ddx + ddy * ddy > lr2) continue;
      const a = clamp(d.life / CONFIG.feel.dmgNumberLife, 0, 1);
      // HG-30: pop-удар (выпрыгивает крупным, оседает) + тёмная обводка — числа читаются на любом фоне
      // и ощущаются как «удар/победа», а не мелкий служебный текст (запрос аудитории №1).
      const age = 1 - a;
      const pop = 1 + (d.crit ? 0.6 : 0.35) * Math.max(0, 1 - age * 7);
      const sz = (d.crit ? 30 : 18) * pop;
      const X = this.sx(d.x), Y = this.sy(d.y);
      const label = d.crit ? (d.value + '!') : '' + d.value;
      ctx.globalAlpha = a;
      ctx.font = this.f(sz.toFixed(0), 700);
      ctx.lineWidth = d.crit ? 4.5 : 3;
      ctx.strokeStyle = 'rgba(18,7,2,0.88)';
      ctx.strokeText(label, X, Y);
      ctx.fillStyle = d.color;
      ctx.fillText(label, X, Y);
      ctx.globalAlpha = 1;
    }
  },

  // координаты мир->экран
  sx(x) { return x - Game.player.x + Game.viewW / 2 + Game.shakeX; },
  sy(y) { return y - Game.player.y + Game.viewH / 2 + Game.shakeY; },

  drawBossBar(ctx, e) {
    const W = Game.viewW, c = CONFIG.colors;
    const bw = Math.min(560, W - 80), bh = 13;
    const x = (W - bw) / 2, y = 60, frac = clamp(e.hp / e.maxHp, 0, 1);
    ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(x, y, bw, bh);
    const g = ctx.createLinearGradient(0, y, 0, y + bh); g.addColorStop(0, '#ff8a3a'); g.addColorStop(1, c.boss);
    ctx.fillStyle = g; ctx.fillRect(x, y, bw * frac, bh);
    this.bracket(ctx, x, y, bw, bh, c.danger, 10, 2, true);
    const bn = CONFIG.enemies[e.typeKey] ? tName('enemies', e.typeKey, CONFIG.enemies[e.typeKey].name).toUpperCase() : t('hud.boss');
    this.heatText(ctx, bn, W / 2, y - 6, 14, 600, c.boss, 'center', 'rgba(255,107,26,0.4)');
  },

  // ----------------------------- HUD -----------------------------
  drawHUD(ctx) {
    const W = Game.viewW, H = Game.viewH, p = Game.player;
    const c = CONFIG.colors;
    // XP-полоса сверху во всю ширину — градиент жара (тлеющий→янтарь→бело-горячий край)
    const xpFrac = clamp(p.xp / p.xpNext, 0, 1);
    ctx.fillStyle = 'rgba(255,176,46,0.10)';
    ctx.fillRect(0, 0, W, 5);
    const xg = ctx.createLinearGradient(0, 0, W * xpFrac, 0);
    xg.addColorStop(0, c.emberDim); xg.addColorStop(0.7, c.ember); xg.addColorStop(1, c.hot);
    ctx.fillStyle = xg; ctx.fillRect(0, 0, W * xpFrac, 5);
    const xpPulse = Game.effects.find(e => e.kind === 'xppulse');
    if (xpPulse) {
      const a = clamp(xpPulse.life / xpPulse.maxLife, 0, 1);
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = 'rgba(246,239,227,' + (0.55 * a).toFixed(3) + ')';
      ctx.fillRect(0, 0, W * xpFrac, 5);
      ctx.globalCompositeOperation = 'source-over';
    }

    // уровень (верх-лево, Oswald)
    this.heatText(ctx, t('hud.level', { n: p.level }), 14, 30, 20, 600, c.ember, 'left');
    // глубина + аномалии (под уровнем)
    const rm = Game.runMods;
    if (rm) {
      let label = '';
      if (rm.depth > 0 && CONFIG.depths[rm.depth - 1]) label += tName('depths', rm.depth - 1, CONFIG.depths[rm.depth - 1].name);
      if (rm.anomalies && rm.anomalies.length) label += (label ? '  ' : '') + rm.anomalies.map(a => a.icon).join(' ');
      if (label) this.heatText(ctx, label, 14, 48, 12, 300, c.boss, 'left');
    }

    // таймер (центр верх) — крупный Oswald, бело-горячий (золото после Рассвета)
    this.heatText(ctx, fmtTime(Game.time), W / 2, 34, 30, 700, Game.reachedDawn ? c.gold : c.hot, 'center', Game.reachedDawn ? 'rgba(255,200,58,0.4)' : undefined);
    if (Game.reachedDawn) this.heatText(ctx, t('hud.eternalNight'), W / 2, 50, 11, 300, c.xp, 'center');
    if (Game.bless) this.heatText(ctx, t('hud.lightPower', { s: Math.ceil(Game.bless.life) }), W / 2, Game.reachedDawn ? 64 : 50, 12, 500, c.hotBlue, 'center');

    // киллы (верх-право) — skull-клеймо + число (кармин); золото под ним (янтарь)
    this.icon(ctx, 'skull', W - 22, 24, 15, c.danger);
    this.heatText(ctx, '' + p.kills, W - 34, 30, 18, 600, c.danger, 'right');
    this.icon(ctx, 'coin', W - 22, 46, 13, c.gold);
    this.heatText(ctx, '' + p.gold, W - 34, 51, 15, 600, c.gold, 'right');

    // ===== VITALS-БРЕКЕТ (низ-лево): жар-ядро = HP крупно + gauge радиуса света. Якорь выживания. =====
    const vw = Math.min(W * 0.32, 300), vx = 16, vh = 64, vy = H - vh - 16;
    this.plate(ctx, vx, vy, vw, vh, { veins: 0.35 });
    this.bracket(ctx, vx, vy, vw, vh, c.emberDim, 12, 2);
    // ядро-иконка (бело-голубой жар)
    ctx.globalCompositeOperation = 'lighter';
    Render.blit(ctx, Render.glowDot(c.hotBlue, 26), vx + 26, vy + 24, 0, 1, 0.7);
    ctx.globalCompositeOperation = 'source-over';
    // HP-бар (целостность ядра) — крупно
    const hbX = vx + 50, hbW = vw - 50 - 16, hpFrac = clamp(p.hp / p.maxHp, 0, 1);
    this.heatText(ctx, t('hud.coreLabel'), hbX, vy + 19, 11, 300, c.ash, 'left');
    this.heatText(ctx, Math.ceil(p.hp) + ' / ' + p.maxHp, vx + vw - 16, vy + 20, 16, 600, hpFrac < 0.3 ? c.danger : c.hot, 'right');
    ctx.fillStyle = c.hpBack; ctx.fillRect(hbX, vy + 26, hbW, 12);
    const hg = ctx.createLinearGradient(0, 0, 0, 12); hg.addColorStop(0, '#ff7a5d'); hg.addColorStop(1, c.hp);
    ctx.fillStyle = hg; ctx.fillRect(hbX, vy + 26, hbW * hpFrac, 12);
    ctx.lineWidth = 1; ctx.strokeStyle = c.plateLo; ctx.strokeRect(hbX, vy + 26, hbW, 12);
    // gauge радиуса света (жар) — тонкая полоса под HP
    const lFrac = clamp((p.light - CONFIG.light.min) / (CONFIG.light.max - CONFIG.light.min), 0, 1);
    ctx.fillStyle = 'rgba(255,176,46,0.12)'; ctx.fillRect(hbX, vy + 44, hbW, 5);
    const lg = ctx.createLinearGradient(hbX, 0, hbX + hbW * lFrac, 0);
    lg.addColorStop(0, c.emberDim); lg.addColorStop(1, c.hotBlue);
    ctx.fillStyle = lg; ctx.fillRect(hbX, vy + 44, hbW * lFrac, 5);
    this.heatText(ctx, t('hud.heatLabel', { n: Math.round(lFrac * 100) }), hbX, vy + 60, 9, 300, c.ash, 'left');
    // МАРКЕР ПОЛЮСА (OGD: читаемый сигнал состояния оси — иначе тёмный билд играет вслепую до смерти).
    // Показывает ТЕКУЩИЙ бонус урона полюса: ярость растёт когда жар падает (тёмный), заря — когда жар широк.
    const loE = (p._loEdge || 0) + (p._synLo || 0), hiE = (p._hiEdge || 0) + (p._synHi || 0);
    if (loE > 0) {
      const lf = CONFIG.light.loFloor || 0;
      const b = loE * (lf + (1 - lf) * (1 - p.light / CONFIG.light.max));
      this.heatText(ctx, t('hud.poleLo', { n: Math.round(b * 100) }), vx + vw - 16, vy + 60, 10, 600, '#ff7a3a', 'right');
    } else if (hiE > 0) {
      this.heatText(ctx, t('hud.poleHi', { n: Math.round(hiE * (p.light / CONFIG.light.max) * 100) }), vx + vw - 16, vy + 60, 10, 600, '#ffd98a', 'right');
    }

    // ===== LOADOUT-БРЕКЕТ (низ-право): кованые слоты билда =====
    this.drawLoadout(ctx);
  },

  drawLoadout(ctx) {
    const p = Game.player, c = CONFIG.colors;
    const W = Game.viewW, H = Game.viewH;
    const slots: any[] = [];
    for (const w of p.weapons) { const def = weaponDef(w.key); slots.push([def.icon, def.color, w.level, false]); }
    for (const key in p.passives) { const def = CONFIG.passives[key]; slots.push([def.icon, def.color, p.passives[key], true]); }
    if (Game.relicsOwned && Game.relicsOwned.length)
      for (const key of Game.relicsOwned) { const rd = CONFIG.relics[key]; if (rd) slots.push([rd.icon, rd.color, '✦', false, true]); }
    if (!slots.length) return;
    const sz = 40, gap = 7, pad = 11;
    const lw = slots.length * sz + (slots.length - 1) * gap + pad * 2;
    const ly = H - sz - pad * 2 - 16, lx = W - lw - 16;
    this.plate(ctx, lx, ly, lw, sz + pad * 2);
    this.bracket(ctx, lx, ly, lw, sz + pad * 2, c.emberDim, 11, 2);
    let cx = lx + pad;
    for (const [icon, color, level, isPass] of slots) {
      this.drawSlot(ctx, cx, ly + pad, icon, color, level, isPass);
      cx += sz + gap;
    }
  },

  drawSlot(ctx, x, y, icon, color, level, isPass = false) {
    const c = CONFIG.colors, sz = 40;
    this.plate(ctx, x, y, sz, sz, { fill0: 'rgba(20,14,8,0.9)', fill1: 'rgba(10,7,4,0.92)', hi: isPass ? 'rgba(122,130,181,0.35)' : c.plateHi });
    this.glyphToken(ctx, icon, x + sz / 2, y + sz / 2 - 2, 26, color, 21);
    // пип уровня — бело-горячий в углу
    this.heatText(ctx, '' + level, x + sz - 5, y + sz - 4, 11, 600, c.hot, 'right');
  },

  drawBanner(ctx) {
    if (Game.bannerTime <= 0) return;
    const a = clamp(Game.bannerTime / Game.bannerMax, 0, 1);
    ctx.globalAlpha = Math.min(1, a * 2);
    this.heatText(ctx, Game.bannerText, Game.viewW / 2, Game.viewH * 0.3, 36, 700, CONFIG.colors.boss, 'center', 'rgba(255,107,26,0.5)');
    ctx.globalAlpha = 1;
  },

  // визуал сенсорного джойстика
  drawJoystick(ctx) {
    if (!Input.touchActive) return;
    const bx = Input.baseX, by = Input.baseY, max = Input.maxRadius;
    let dx = Input.curX - bx, dy = Input.curY - by;
    const d = Math.hypot(dx, dy);
    if (d > max) { dx = dx / d * max; dy = dy / d * max; }
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = CONFIG.colors.player; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(bx, by, max, 0, TAU); ctx.stroke();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = CONFIG.colors.player;
    ctx.beginPath(); ctx.arc(bx + dx, by + dy, 24, 0, TAU); ctx.fill();
    ctx.globalAlpha = 1;
  },

  // ----------------------------- ОВЕРЛЕИ -----------------------------
  dim(ctx, a = 0.66) {
    ctx.fillStyle = 'rgba(4,6,12,' + a + ')';
    ctx.fillRect(0, 0, Game.viewW, Game.viewH);
  },

  drawMenu(ctx) {
    const W = Game.viewW, H = Game.viewH;
    const clk = Game.clock || 0;
    ctx.textAlign = 'center';
    // ЖИВАЯ АРЕНА-ФОН (HG-15): тлеющее горнило снизу + дрейфующие вверх угольки — «костёр живёт»,
    // не пустой тёмный экран-шаблон. Намёк на движение/жар до первого клика.
    ctx.globalCompositeOperation = 'lighter';
    Render.blit(ctx, Render.glowDot('#ff7a2a', 230), W / 2, H + 90, 0, 1, 0.20);   // нижнее горнило
    for (let i = 0; i < 46; i++) {
      const seed = i * 97.13, sp = 16 + (i % 5) * 8;
      const yy = H - ((clk * sp + seed * 13) % (H + 90));
      const xx = ((seed * 57) % W) + Math.sin(clk * 0.7 + i) * 20;
      const fl = 0.4 + 0.4 * Math.sin(clk * 3 + i), sz = 1.4 + (i % 3);
      Render.blit(ctx, Render.glowDot(i % 4 ? '#ff9a3a' : '#ffd27a', sz * 3), xx, yy, 0, 1, 0.08 + 0.16 * fl);
    }
    ctx.globalCompositeOperation = 'source-over';
    const c = CONFIG.colors;
    // ГОРНИЛО-меню: бренд приклёпан верх-лево (центр сломан), ember-ядро справа, плиты-кнопки слева.
    const bx = Math.max(48, W * 0.06);
    // ember-ядро справа (тёплый горн — регистр WARM в меню)
    ctx.globalCompositeOperation = 'lighter';
    Render.blit(ctx, Render.glowDot('#ff8a2a', 200), W * 0.74, H * 0.46, 0, 1, 0.5);
    Render.blit(ctx, Render.glowDot(c.hotBlue, 60), W * 0.74, H * 0.46, 0, 1, 0.28);
    // ореол за брендом
    Render.blit(ctx, Render.glowDot(c.ember, 70), bx + W * 0.12, H * 0.22, 0, 1, 0.22);
    ctx.globalCompositeOperation = 'source-over';

    // БРЕНД «ПЕПЕЛИЩЕ» — Oswald 700, последний слог бело-горячий (апекс жара)
    const tSize = Math.min(112, W * 0.088);
    const ty = H * 0.24;
    ctx.textAlign = 'left';
    ctx.font = this.f(tSize, 700);
    const w1 = ctx.measureText('ПЕПЕ').width;
    ctx.shadowColor = 'rgba(255,150,40,0.45)'; ctx.shadowBlur = tSize * 0.4;
    ctx.fillStyle = c.ember; ctx.fillText('ПЕПЕ', bx, ty);
    ctx.shadowColor = 'rgba(207,224,255,0.5)';
    ctx.fillStyle = c.hot; ctx.fillText('ЛИЩЕ', bx + w1, ty);
    ctx.shadowBlur = 0;
    // подзаголовок (разрядка) + слоган + голос
    this.heatText(ctx, 'S U R V I V O R S', bx + 4, ty + tSize * 0.34, Math.max(14, tSize * 0.18), 200, c.emberDim, 'left');
    this.heatText(ctx, t('menu.tagline'), bx + 2, ty + tSize * 0.34 + 30, 18, 500, c.ember, 'left');
    if (Game.menuVoice)
      this.heatText(ctx, Game.menuVoice, bx + 2, ty + tSize * 0.34 + 54, 14, 300, c.ash, 'left');

    // ПЛИТЫ-КНОПКИ — левый якорь, вертикальный стек
    const btnW = Math.min(340, W * 0.32), btnX = bx;
    let byy = H * 0.56;
    const playHover = this._hover(btnX, byy, btnW, 54);
    Game._menuPlayRect = this.stamp(ctx, btnX, byy, btnW, 54, t('menu.start'), { prime: true, hover: playHover, size: 23, icon: 'play' });
    byy += 54 + 14;
    const shopHover = this._hover(btnX, byy, btnW, 44);
    Game._menuShopRect = this.stamp(ctx, btnX, byy, btnW, 44, t('menu.shop'), { hover: shopHover, size: 17, icon: 'forge', color: c.gold });
    byy += 44 + 12;
    const stHover = this._hover(btnX, byy, btnW, 44);
    Game._menuSettingsRect = this.stamp(ctx, btnX, byy, btnW, 44, t('menu.settings'), { hover: stHover, size: 16, icon: 'gear', color: c.ash });

    // строка управления (тускло, под кнопками)
    const touch = ('ontouchstart' in window) || Input.isTouch;
    this.heatText(ctx, touch ? t('menu.moveTouch') : t('menu.moveKeyboard'), btnX + 2, byy + 44 + 30, 13, 300, c.ash, 'left');

    // STATBAR — обугленная плита-readout, приклёпана низ (со скобками-углами)
    if (typeof Meta !== 'undefined' && Meta.data.best) {
      const codexN = Object.keys(Meta.data.codex || {}).length;
      const totalTypes = Object.keys(CONFIG.enemies).length;
      const cells: any[] = [[t('menu.best').replace(/[:：].*$/, '') || 'BEST', fmtTime(Meta.data.best)]];
      if (Meta.data.maxDepth > 0) cells.push([t('menu.depthStat').replace(/[:：].*$/, '') || 'DEPTH', Meta.data.maxDepth + ' / ' + CONFIG.depths.length]);
      cells.push([t('menu.codexStat').replace(/[:：].*$/, '') || 'CODEX', codexN + ' / ' + totalTypes]);
      const sbH2 = 58, sbY2 = H - sbH2 - 30, sbW2 = Math.min(W - bx * 2, 560), sbX2 = bx;
      this.plate(ctx, sbX2, sbY2, sbW2, sbH2, { veins: 0.35 });
      this.bracket(ctx, sbX2, sbY2, sbW2, sbH2, c.emberDim, 12, 2);
      let cxp = sbX2 + 22;
      for (const [lab, val] of cells) {
        this.heatText(ctx, ('' + lab).toUpperCase(), cxp, sbY2 + 22, 11, 300, c.ash, 'left');
        this.heatText(ctx, '' + val, cxp, sbY2 + 46, 24, 600, c.hot, 'left');
        cxp += Math.max(120, ctx.measureText('' + val).width + 70);
      }
    }
  },

  // ряд выбора языка: «LANGUAGE  RU EN DE FR», текущий подсвечен и подчёркнут.
  // Кликабельные зоны кладёт в Game._langRects (обрабатывает main.ts).
  drawLangRow(ctx, cx, y, labelKey) {
    ctx.textAlign = 'left';
    ctx.font = this.f(13, 600);
    const label = t(labelKey) + '  ';
    const gap = 16;
    const codes = LANGS.map(l => LANG_LABELS[l]);
    const labelW = ctx.measureText(label).width;
    const codeW = codes.map(s => ctx.measureText(s).width);
    const total = labelW + codeW.reduce((a, b) => a + b, 0) + gap * (LANGS.length - 1);
    let x = cx - total / 2;
    ctx.fillStyle = CONFIG.colors.textDim;
    ctx.fillText(label, x, y);
    x += labelW;
    Game._langRects = [];
    for (let i = 0; i < LANGS.length; i++) {
      const lang = LANGS[i], w = codeW[i], sel = Locale.current === lang;
      ctx.fillStyle = sel ? CONFIG.colors.player : CONFIG.colors.textDim;
      ctx.fillText(codes[i], x, y);
      if (sel) ctx.fillRect(x, y + 4, w, 2);
      Game._langRects.push({ lang, x: x - gap / 2, y: y - 13, w: w + gap, h: 20 });
      x += w + gap;
    }
    ctx.textAlign = 'center';
  },

  drawCharSelect(ctx) {
    const W = Game.viewW, H = Game.viewH, c = CONFIG.colors;
    this.heatText(ctx, t('charselect.title'), W / 2, H * 0.2, 42, 700, c.hot, 'center', 'rgba(255,150,40,0.35)');
    this.heatText(ctx, t('charselect.controls'), W / 2, H * 0.2 + 26, 14, 300, c.ash, 'center');

    const keys = Object.keys(CONFIG.characters);
    const n = keys.length, gap = 24, ch = 264;
    const cw = Math.min(238, (W - 80 - (n - 1) * gap) / n);   // адаптивно под число героев
    const totalW = n * cw + (n - 1) * gap, x0 = (W - totalW) / 2, y0 = H * 0.28;
    Game._charRects = [];
    for (let i = 0; i < n; i++) {
      const c = CONFIG.characters[keys[i]];
      const locked = (typeof Meta !== 'undefined') && !Meta.isUnlocked(keys[i]);
      const x = x0 + i * (cw + gap);
      const sel = i === Game.charIndex;
      const COL = CONFIG.colors;
      this.plate(ctx, x, y0, cw, ch, {
        fill0: sel ? 'rgba(40,29,14,0.95)' : 'rgba(24,17,9,0.88)', fill1: 'rgba(11,8,5,0.93)',
        hi: sel && !locked ? COL.hot : COL.plateHi, bevel: sel ? 2.5 : 1.5, veins: sel ? 0.4 : 0.2,
      });
      if (sel) this.bracket(ctx, x, y0, cw, ch, locked ? COL.ash : COL.hot, 14, 2);
      ctx.textAlign = 'center';
      if (locked) {
        ctx.globalAlpha = 0.6; this.icon(ctx, 'lock', x + cw / 2, y0 + 90, 46, COL.ash); ctx.globalAlpha = 1;
        this.heatText(ctx, t('charselect.locked'), x + cw / 2, y0 + 150, 21, 600, COL.ash, 'center');
        const ud = CONFIG.unlocks.find(u => u.key === keys[i]);
        ctx.fillStyle = COL.gold; ctx.font = this.f(12, 400, true);
        this.wrapText(ctx, ud ? tHint(keys[i], ud.hint) : t('charselect.lockedFallback'), x + cw / 2, y0 + 184, cw - 26, 17);
      } else {
        this.glyphToken(ctx, c.icon, x + cw / 2, y0 + 88, sel ? 56 : 46, c.color, sel ? 54 : 50);
        this.heatText(ctx, tName('characters', keys[i], c.name), x + cw / 2, y0 + 150, 22, 600, sel ? COL.hot : COL.ember, 'center');
        ctx.fillStyle = COL.ash; ctx.font = this.f(12, 400, true); ctx.textAlign = 'center';
        this.wrapText(ctx, tDesc('characters', keys[i], c.desc), x + cw / 2, y0 + 178, cw - 26, 17);
      }
      Game._charRects.push({ x, y: y0, w: cw, h: ch });
    }

    // выбор Глубины Тьмы (ascension) — открывается победами
    const maxD = (typeof Meta !== 'undefined') ? Meta.data.maxDepth : 0;
    const dy = y0 + ch + 42;
    Game.selectedDepth = clamp(Game.selectedDepth, 0, maxD);
    const dn = Game.selectedDepth === 0
      ? t('charselect.surface')
      : tName('depths', Game.selectedDepth - 1, CONFIG.depths[Game.selectedDepth - 1].name);
    ctx.textAlign = 'center';
    Game._depthRects = null;
    if (maxD > 0) {
      ctx.fillStyle = CONFIG.colors.textDim; ctx.font = '13px Consolas, monospace';
      ctx.fillText(t('charselect.depthTitle'), W / 2, dy - 18);
      ctx.fillStyle = Game.selectedDepth ? CONFIG.colors.boss : CONFIG.colors.xp;
      ctx.font = 'bold 22px Consolas, monospace';
      ctx.fillText('◂  ' + dn + '  ▸', W / 2, dy + 8);
      // кликабельные стрелки изменения глубины
      Game._depthRects = {
        left:  { x: W / 2 - 150, y: dy - 12, w: 70, h: 34 },
        right: { x: W / 2 + 80,  y: dy - 12, w: 70, h: 34 },
      };
      if (Game.selectedDepth > 0) {
        ctx.fillStyle = CONFIG.colors.textDim; ctx.font = '12px Consolas, monospace';
        ctx.fillText(t('charselect.depthReward', {
          desc: tDesc('depths', Game.selectedDepth - 1, CONFIG.depths[Game.selectedDepth - 1].desc),
          mult: CONFIG.depths[Game.selectedDepth - 1].reward.toFixed(2),
        }), W / 2, dy + 28);
      }
    } else {
      ctx.fillStyle = CONFIG.colors.textDim; ctx.font = '12px Consolas, monospace';
      ctx.fillText(t('charselect.depthLocked'), W / 2, dy);
    }

    // выбор ПУТИ (стартовый архетип) — ТРЕТЬЯ ось (мета-как-способы). ОНБОРДИНГ: селектор СКРЫТ,
    // пока не разблокирован хоть один Путь кроме Странника — первый экран чист (только герои).
    const anyPath = (typeof Meta !== 'undefined') && CONFIG.paths.some((p: any) => p.key !== 'wanderer' && Meta.isUnlocked(p.key));
    Game._pathRects = null;
    if (!anyPath) { Game.selectedPath = 0; return; }
    const pdy = dy + 50;
    Game.selectedPath = clamp(Game.selectedPath || 0, 0, CONFIG.paths.length - 1);
    const pdef = CONFIG.paths[Game.selectedPath];
    const pUnlocked = (typeof Meta === 'undefined') || Meta.isUnlocked(pdef.key);
    ctx.textAlign = 'center';
    ctx.fillStyle = CONFIG.colors.textDim; ctx.font = '13px Consolas, monospace';
    ctx.fillText(t('charselect.pathTitle'), W / 2, pdy - 18);
    ctx.fillStyle = pUnlocked ? (pdef.color || CONFIG.colors.xp) : CONFIG.colors.textDim;
    ctx.font = 'bold 20px Consolas, monospace';
    const pname = pUnlocked ? (pdef.icon + '  ' + tName('paths', pdef.key, pdef.name)) : t('charselect.locked');
    ctx.fillText('◂  ' + pname + '  ▸', W / 2, pdy + 6);
    Game._pathRects = {
      left:  { x: W / 2 - 170, y: pdy - 14, w: 72, h: 34 },
      right: { x: W / 2 + 98,  y: pdy - 14, w: 72, h: 34 },
    };
    ctx.fillStyle = CONFIG.colors.textDim; ctx.font = '12px Consolas, monospace';
    const ptxt = pUnlocked
      ? tDesc('paths', pdef.key, pdef.desc)
      : tHint(pdef.key, (CONFIG.unlocks.find((u: any) => u.key === pdef.key) || {}).hint || '');
    ctx.fillText(ptxt, W / 2, pdy + 26);
  },

  drawShop(ctx) {
    const W = Game.viewW, H = Game.viewH, c = CONFIG.colors;
    this.heatText(ctx, t('shop.title'), W / 2, H * 0.09, 36, 700, c.hot, 'center', 'rgba(255,150,40,0.35)');
    Game._shopBackRect = this.stamp(ctx, 24, 18, 120, 34, t('shop.back'), { hover: this._hover(24, 18, 120, 34), size: 14, color: c.ash, icon: 'back' });
    // золото — heat-readout
    this.icon(ctx, 'coin', W / 2 - 44, H * 0.09 + 24, 18, c.gold);
    this.heatText(ctx, '' + (typeof Meta !== 'undefined' ? Meta.data.gold : 0), W / 2 - 28, H * 0.09 + 30, 22, 700, c.gold, 'left');
    this.heatText(ctx, t('shop.controls'), W / 2, H * 0.09 + 52, 12.5, 300, c.ash, 'center');

    // ДВА ТРЕКА в колонках: КУЗНИЦА (статы) | ГОРНИЛО (горизонталь). Заголовок над каждой.
    const colW = Math.min(430, (W - 64) / 2), gapX = 28;
    const totalW = colW * 2 + gapX;
    const x0 = (W - totalW) / 2;
    const headY = H * 0.24, rowH = 56, gap = 9, rowY0 = headY + 22;
    this.heatText(ctx, t('shop.trackForge'),  x0 + colW / 2,               headY, 13, 700, c.emberDim, 'center');
    this.heatText(ctx, t('shop.trackHearth'), x0 + colW + gapX + colW / 2, headY, 13, 700, c.emberDim, 'center');
    Game._shopRects = [];
    const counts: any = { forge: 0, hearth: 0 };
    for (let i = 0; i < CONFIG.shop.length; i++) {
      const u = CONFIG.shop[i];
      const track = u.track === 'hearth' ? 'hearth' : 'forge';
      const x = x0 + (track === 'hearth' ? colW + gapX : 0);
      const y = rowY0 + (counts[track]++) * (rowH + gap);
      const lv = Meta.upgLevel(u.key), cost = Meta.upgCost(u.key);
      const sel = i === Game.shopIndex;
      const afford = cost != null && Meta.data.gold >= cost;
      this.plate(ctx, x, y, colW, rowH, { fill0: sel ? 'rgba(40,29,14,0.95)' : 'rgba(22,16,9,0.88)', fill1: 'rgba(11,8,5,0.92)', hi: sel ? c.hot : c.plateHi, bevel: sel ? 2.5 : 1.5, veins: sel ? 0.35 : 0 });
      if (sel) this.bracket(ctx, x, y, colW, rowH, c.hot, 12, 2);
      this.glyphToken(ctx, u.icon, x + 30, y + rowH / 2, 28, u.color, 24);
      this.heatText(ctx, tName('shop', u.key, u.name), x + 56, y + 22, 16, 600, sel ? c.hot : c.ember, 'left');
      ctx.fillStyle = c.ash; ctx.font = this.f(11, 400, true); ctx.textAlign = 'left';
      ctx.fillText(tDesc('shop', u.key, u.desc), x + 56, y + 40);
      // пипы уровня
      const pipW = 11, px = x + colW - 14 - u.max * pipW;
      for (let k = 0; k < u.max; k++) { ctx.fillStyle = k < lv ? u.color : 'rgba(255,255,255,0.12)'; ctx.fillRect(px + k * pipW, y + rowH - 16, pipW - 3, 7); }
      // цена / MAX (правый верх ряда)
      if (cost == null) this.heatText(ctx, t('shop.max'), x + colW - 14, y + 22, 14, 600, c.xp, 'right');
      else { this.heatText(ctx, '' + cost, x + colW - 14, y + 22, 15, 600, afford ? c.gold : c.ash, 'right');
        this.icon(ctx, 'coin', x + colW - 16 - ctx.measureText('' + cost).width - 12, y + 16, 12, afford ? c.gold : c.ash); }
      Game._shopRects.push({ x, y, w: colW, h: rowH });
    }
  },

  drawDraft(ctx) {
    const W = Game.viewW, H = Game.viewH;
    this.dim(ctx, 0.72);
    // вспышка-пульс при открытии (левелап/развилка/святилище) — Game.clock тикает и во фризе.
    // Эволюция — весомейшее событие забега — вспыхивает ярче, золотом и дольше.
    const isEvoFlash = Game.offers.some(o => o.type === 'evolve');
    const fDur = isEvoFlash ? 0.5 : 0.35;
    const fAge = (Game.clock || 0) - (Game._draftOpenClock || 0);
    if (fAge >= 0 && fAge < fDur) {
      const fa = (isEvoFlash ? 0.34 : 0.22) * (1 - fAge / fDur);
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = (isEvoFlash ? 'rgba(255,216,90,' : 'rgba(255,236,170,') + fa.toFixed(3) + ')';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'source-over';
    }
    const c = CONFIG.colors;
    const isEv = Game._draftIsEvent;
    const isSnare = Game._draftIsSnare;
    const isSh = Game._draftIsShrine && !isSnare;   // захват тоже ставит _draftIsShrine — но у него свой заголовок
    const isAnom = Game._draftIsAnomaly;
    const titleTemp = isSnare ? c.danger : (isAnom ? c.vow : (isSh ? c.xp : (isEv ? c.boss : c.hot)));
    const snareDefend = isSnare && Game.snareMode === 'defend';   // архетип уже выбран в triggerShrine до драфта
    const title = isSnare ? t(snareDefend ? 'draft.snareDefendTitle' : 'draft.snareTitle') : (isAnom ? t('draft.anomalyTitle')
      : (isSh ? t('draft.shrineTitle') : (isEv ? t('draft.eventTitle') : t('draft.levelTitle', { n: Game.player.level }))));
    this.heatText(ctx, title, W / 2, H * 0.2, 40, 700, titleTemp, 'center', isAnom ? 'rgba(122,130,181,0.4)' : 'rgba(255,150,40,0.4)');
    const hint = isSnare ? t(snareDefend ? 'draft.snareDefendHint' : 'draft.snareHint') : (isAnom ? t('draft.anomalyHint', { n: Game._anomalyPicksLeft })
      : (isSh ? t('draft.shrineHint') : (isEv ? t('draft.eventHint') : t('draft.levelHint'))));
    this.heatText(ctx, hint, W / 2, H * 0.2 + 26, 13, 300, c.ash, 'center');
    // голос святилища (§14.3) — загадочная строка перед выбором реликвии
    if (isSh && Game.draftVoice) {
      ctx.fillStyle = c.ember; ctx.font = this.f(15, 400, true); ctx.textAlign = 'center';
      ctx.fillText('« ' + Game.draftVoice + ' »', W / 2, H * 0.2 + 50);
    }

    const n = Game.offers.length;
    const cw = 210, gap = 26, ch = 258;
    const totalW = n * cw + (n - 1) * gap;
    const x0 = (W - totalW) / 2;
    const y0 = H * 0.3;
    Game._cardRects = [];
    for (let i = 0; i < n; i++) {
      const x = x0 + i * (cw + gap);
      const sel = i === Game.selIndex;
      this.drawCard(ctx, Game.offers[i], x, y0, cw, ch, sel);
      Game._cardRects.push({ x, y: y0 - (sel ? 12 : 0), w: cw, h: ch + (sel ? 12 : 0) });
    }

    // кнопка пересбора (раз за забег) — клеймо под рядом
    Game._rerollRect = null;
    if (Game.canReroll && Game.canReroll()) {
      const bw = 210, bh = 38, bx = W / 2 - bw / 2, by = y0 + ch + 22;
      Game._rerollRect = this.stamp(ctx, bx, by, bw, bh, t('draft.reroll'), { hover: Game._rerollHover, size: 14, icon: 'reroll', color: c.ash });
    }
  },

  // дельта значений при повышении уровня (что именно растёт: «было → стало»).
  // Только числа и нейтральные символы (+ − % × →) — не требует локализации.
  // единица измерения дельты по пассивке (локализована в ui: unit.*) — делает голое число осмысленным
  _passiveUnit(key: string) {
    const m: any = { power: 'dmg', speed: 'speed', regen: 'regen', magnet: 'radius', vigor: 'hp',
      crit: 'crit', haste: 'cooldown', leech: 'leech', bloom: 'dmg', frost: 'enemySlow', pulse: 'dmg' };
    return t(('unit.' + (m[key] || 'dmg')) as any);
  },
  // «что на сколько»: НОВАЯ карта (lv1) — значение 1-го уровня; апгрейд (lv2-5) — было→стало.
  // К числу приписывается локализованная единица (16 урона / −18% к скорости врагов / +25 макс. HP).
  upgradeDelta(o) {
    const lv = o.resLvl, isNew = lv <= 1;
    if (o.type === 'weapon') {
      const d = CONFIG.weapons[o.key];
      if (!d.dmg || d.dmg[lv - 1] == null) return '';
      const u = ' ' + t('unit.dmg');
      if (isNew) return d.dmg[0] + u + (d.count && d.count[0] > 1 ? ' ×' + d.count[0] : '');
      let s = d.dmg[lv - 2] + ' → ' + d.dmg[lv - 1] + u;
      if (d.count && d.count[lv - 2] !== d.count[lv - 1]) s += '  ×' + d.count[lv - 2] + '→×' + d.count[lv - 1];
      return s;
    }
    if (o.type === 'passive') {
      const def = CONFIG.passives[o.key];
      const b = def.val[lv - 1]; if (b == null) return '';
      const a = def.val[lv - 2], u = ' ' + this._passiveUnit(o.key);
      const pct = (v: number) => Math.round((v - 1) * 100);
      if (def.mode === 'mult')     return (isNew ? '+' + pct(b) + '%' : '+' + pct(a) + '% → +' + pct(b) + '%') + u;
      if (def.mode === 'add')      return (isNew ? '+' + b : '+' + a + ' → +' + b) + u;            // регенерация
      if (def.mode === 'addMaxHp') return (isNew ? '+' + b : '+' + a + ' → +' + b) + u;            // макс. HP
      if (def.mode === 'special') {
        if (def.special === 'crit')  return (isNew ? Math.round(b * 100) + '%' : Math.round(a * 100) + '% → ' + Math.round(b * 100) + '%') + u;
        if (def.special === 'haste') return (isNew ? '−' + Math.round((1 - b) * 100) + '%' : '−' + Math.round((1 - a) * 100) + '% → −' + Math.round((1 - b) * 100) + '%') + u;
        if (def.special === 'frost') return (isNew ? '−' + Math.round((1 - b) * 100) + '%' : '−' + Math.round((1 - a) * 100) + '% → −' + Math.round((1 - b) * 100) + '%') + u;
        return (isNew ? '' + b : a + ' → ' + b) + u;   // leech / bloom / pulse — абсолютные числа + единица
      }
    }
    return '';
  },

  // подобрать размер шрифта так, чтобы текст влез в maxW (не ниже minSize).
  // Сам выставляет ctx.font; вызывающий после задаёт fillStyle и рисует.
  fitFont(ctx, text, maxW, baseSize, weight: any = 600, minSize = 12) {
    let size = baseSize;
    const wt = (weight === '' || weight === 'bold ') ? 600 : weight;
    ctx.font = this.f(size, wt);
    while (size > minSize && ctx.measureText(text).width > maxW) {
      size--;
      ctx.font = this.f(size, wt);
    }
    return size;
  },

  offerDisplay(o) {
    if (o.type === 'anomaly') {
      const d = o.def;
      return { icon: d.icon, color: CONFIG.colors.vow, name: tName('anomalies', d.key, d.name), kind: t('kind.anomaly'),
               desc: tDesc('anomalies', d.key, d.desc), lvl: 0, isNew: false, evo: true };
    }
    if (o.type === 'event') {
      const d = o.def;
      return { icon: d.icon, color: d.color, name: tName('darkEvents', o.kind, d.name), kind: t('kind.event'),
               desc: tDesc('darkEvents', o.kind, d.desc), lvl: 0, isNew: false };
    }
    if (o.type === 'evolve') {
      const d = CONFIG.evolutions[o.into];
      return { icon: d.icon, color: d.color, name: tName('evolutions', o.into, d.name), kind: t('kind.evolve'),
               desc: tDesc('evolutions', o.into, d.desc), lvl: 0, isNew: false, evo: true };
    }
    if (o.type === 'relic') {
      const d = CONFIG.relics[o.key];
      return { icon: d.icon, color: d.color, name: tName('relics', o.key, d.name), kind: t('kind.relic'),
               desc: tDesc('relics', o.key, d.desc), lvl: 0, isNew: false, evo: true };
    }
    if (o.type === 'weapon') {
      const d = CONFIG.weapons[o.key];
      // описание показываем ВСЕГДА (что делает оружие), а при повышении уровня
      // дополняем дельтой ключевого стата (от чего к чему) — числа нейтральны к языку.
      return { icon: d.icon, color: d.color, name: tName('weapons', o.key, d.name), kind: t('kind.weapon'),
               desc: tDesc('weapons', o.key, d.desc), delta: this.upgradeDelta(o),
               lvl: o.resLvl, isNew: o.isNew };
    }
    if (o.type === 'passive') {
      const d = CONFIG.passives[o.key];
      return { icon: d.icon, color: d.color, name: tName('passives', o.key, d.name), kind: t('kind.passive'),
               desc: tDesc('passives', o.key, d.desc), delta: this.upgradeDelta(o),
               lvl: o.resLvl, isNew: o.isNew };
    }
    if (o.type === 'mod') {
      const d = CONFIG.forgeMods[o.key];
      return { icon: d.icon, color: d.color, name: tName('forgeMods', o.key, d.name), kind: t('kind.forge'),
               desc: tDesc('forgeMods', o.key, d.desc), lvl: 0, isNew: false, evo: true };
    }
    if (o.type === 'overclock') {
      const isEvo = !!CONFIG.evolutions[o.key];
      const def = isEvo ? CONFIG.evolutions[o.key] : CONFIG.weapons[o.key];
      const w = Game.player.weapons.find(x => x.key === o.key);
      const oc = CONFIG.overclock, stack = ((w && w.oc) || 0) + 1;
      const wname = isEvo ? tName('evolutions', o.key, def.name) : tName('weapons', o.key, def.name);
      return { icon: '♨', color: '#ff5a2a', name: t('forge.ocName', { w: wname }), kind: t('kind.overclock'),
               desc: t('forge.ocDesc', { dmg: Math.round(oc.dmgPerStack * 100), cost: oc.lightCost, n: stack, max: oc.maxStacks }),
               lvl: 0, isNew: false, evo: true };
    }
    if (o.type === 'boon') {
      const d = o.def;
      return { icon: d.icon, color: d.color, name: tName('snareBoons', o.key, d.name), kind: t('kind.boon'),
               desc: tDesc('snareBoons', o.key, d.desc), lvl: 0, isNew: false, evo: true };
    }
    return { icon: '✚', color: CONFIG.colors.xp, name: t('heal.name'), kind: t('kind.bonus'),
             desc: t('heal.desc'), lvl: 0, isNew: false };
  },

  // Карта-СЛИТОК (ГОРНИЛО): кованая плита, тир кодируется температурой акцента; выбранная —
  // приподнята «ударом» + бело-горячая со скобками-углами. Завет — стально-сиреневый (холодное правило).
  drawCard(ctx, o, x, y, w, h, sel) {
    const c = CONFIG.colors;
    const d = this.offerDisplay(o);
    const evo = d.evo && o.type !== 'anomaly';
    const rare = !evo && (o.type === 'weapon' || o.type === 'passive') && o.resLvl === 5;
    const isVow = d.color === c.vow || o.type === 'anomaly';
    if (sel) y -= 12;   // лифт выбранного слитка (rect в drawDraft учитывает сдвиг)
    const accent = isVow ? c.vow : (sel ? c.hot : (evo ? c.gold : rare ? c.ember : c.emberDim));
    // плита-слиток с фаской
    this.plate(ctx, x, y, w, h, {
      fill0: sel ? 'rgba(42,30,14,0.96)' : 'rgba(26,19,10,0.9)',
      fill1: 'rgba(11,8,5,0.94)',
      hi: sel ? c.hot : (isVow ? 'rgba(122,130,181,0.32)' : c.plateHi),
      bevel: sel ? 2.5 : 1.5,
      veins: sel ? 0.5 : 0.22,
    });
    // ореол под иконкой (выбор/легенда — ярче). Прижат к иконке (y+72, не лезет в имя y+134) и
    // приглушён на выборе (0.42→0.3) — иначе подмывал текст карточки (засвет).
    ctx.globalCompositeOperation = 'lighter';
    Render.blit(ctx, Render.glowDot(accent, sel ? 52 : evo || rare ? 46 : 34), x + w / 2, y + 72, 0, 1, sel ? 0.3 : (evo || rare ? 0.24 : 0.14));
    ctx.globalCompositeOperation = 'source-over';
    if (sel) this.bracket(ctx, x, y, w, h, c.hot, 16, 2.5);
    else if (evo || rare || isVow) this.bracket(ctx, x, y, w, h, accent, 12, 1.5);
    // тир (верх-лево) + категория (верх-право)
    const rar = (o.type === 'mod' || o.type === 'overclock') ? '' : evo ? t('rarity.legend') : rare ? t('rarity.rare') : (isVow ? d.kind : '');
    if (rar) this.heatText(ctx, ('' + rar).toUpperCase(), x + 18, y + 24, 10, 300, sel ? c.hot : accent, 'left');
    this.heatText(ctx, (d.kind + (d.isNew ? ' · ' + t('draft.new') : '')).toUpperCase(), x + w - 18, y + 24, 10, 300, isVow ? c.vow : c.emberDim, 'right');
    // иконка-токен
    this.glyphToken(ctx, d.icon, x + w / 2, y + 80, sel ? 58 : 48, accent, sel ? 54 : 48);
    // ТЁМНАЯ ПОДЛОЖКА-ТЕНЬ под весь текст карточки — держит контраст поверх ореола выбора (фикс засвета).
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.82)'; ctx.shadowBlur = 4; ctx.shadowOffsetY = 1;
    // имя (Oswald, авто-фит)
    const nsz = this.fitFont(ctx, d.name, w - 28, 25, 600);
    this.heatText(ctx, d.name, x + w / 2, y + 134, nsz, 600, sel ? c.hot : c.ember, 'center');
    // описание (mono — плотный body; на выборе чуть ярче пепельного для читаемости)
    ctx.fillStyle = sel ? '#cdbfa8' : c.ash; ctx.font = this.f(12.5, 400, true); ctx.textAlign = 'center';
    const descEndY = this.wrapText(ctx, d.desc, x + w / 2, y + 160, w - 30, 17);
    // дельта «что на сколько» (для новых карт — значение 1-го уровня; иначе было → стало)
    if (d.delta) {
      const dsz = this.fitFont(ctx, d.delta, w - 26, 13, 600);
      this.heatText(ctx, d.delta, x + w / 2, descEndY + 22, dsz, 600, sel ? c.hot : accent, 'center');
    }
    ctx.restore();
    // пипы уровня (низ)
    if (d.lvl > 0) {
      const pipW = 14, total = 5 * pipW, px = x + w / 2 - total / 2;
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = i < d.lvl ? accent : 'rgba(255,255,255,0.10)';
        ctx.fillRect(px + i * pipW, y + h - 22, pipW - 4, 5);
      }
    }
  },

  wrapText(ctx, text, cx, y, maxW, lh) {
    const words = text.split(' ');
    let line = '', yy = y;
    for (const wd of words) {
      const test = line ? line + ' ' + wd : wd;
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, cx, yy); line = wd; yy += lh;
      } else line = test;
    }
    if (line) ctx.fillText(line, cx, yy);
    return yy;   // Y последней строки — чтобы класть контент под перенесённым текстом
  },

  // ряд кованых слотов, выровнен по центру (билд в паузе/итогах)
  slotRow(ctx, cx, y, items) {
    const sz = 40, gap = 7;
    let x = cx - (items.length * sz + (items.length - 1) * gap) / 2;
    for (const [icon, color, level, isPass] of items) { this.drawSlot(ctx, x, y, icon, color, level, isPass); x += sz + gap; }
  },

  drawPaused(ctx) {
    this.dim(ctx, 0.74);
    const W = Game.viewW, H = Game.viewH, p = Game.player, c = CONFIG.colors;
    // панель-плита приклёпана (мир за ней заморожен, виден). Заголовок бело-горячий.
    this.heatText(ctx, t('pause.title'), W / 2, H * 0.18, 44, 700, c.hot, 'center', 'rgba(255,150,40,0.35)');
    this.heatText(ctx, t('pause.stats', { lvl: p.level, time: fmtTime(Game.time), kills: p.kills, light: Math.round(p.light) }), W / 2, H * 0.18 + 30, 14, 300, c.ash, 'center');

    // билд: оружие
    this.heatText(ctx, t('pause.weapons'), W / 2, H * 0.32 - 14, 11, 300, c.emberDim, 'center');
    this.slotRow(ctx, W / 2, H * 0.32, p.weapons.map(w => { const def = weaponDef(w.key); return [def.icon, def.color, w.level, false]; }));

    // билд: пассивы
    const pk = Object.keys(p.passives);
    this.heatText(ctx, t('pause.passives'), W / 2, H * 0.44 - 14, 11, 300, c.emberDim, 'center');
    if (pk.length) this.slotRow(ctx, W / 2, H * 0.44, pk.map(key => [CONFIG.passives[key].icon, CONFIG.passives[key].color, p.passives[key], true]));
    else this.heatText(ctx, '—', W / 2, H * 0.44 + 24, 14, 400, c.ash, 'center');

    // реликвии забега
    if (Game.relicsOwned && Game.relicsOwned.length) {
      this.heatText(ctx, t('pause.relics'), W / 2, H * 0.56 - 8, 11, 300, c.emberDim, 'center');
      this.slotRow(ctx, W / 2, H * 0.56, Game.relicsOwned.map(key => CONFIG.relics[key]).filter(Boolean).map(rd => [rd.icon, rd.color, '✦', false]));
    }

    // активные синергии оружия (имена — игрок видит свой собранный билд-комбо)
    if (p._synergies && p._synergies.length) {
      this.heatText(ctx, t('pause.synergies'), W / 2, H * 0.63 - 8, 11, 300, c.emberDim, 'center');
      const names = p._synergies.map((k) => { const s = CONFIG.synergies.find((x) => x.key === k); return s ? (s.icon + ' ' + tName('synergies', k, s.name)) : k; }).join('    ');
      this.heatText(ctx, names, W / 2, H * 0.63 + 12, 14, 500, c.ember, 'center');
    }

    // кнопки — клейма
    Game._pauseRects = {};
    const setW = 220, setX = W / 2 - setW / 2, setY = H * 0.67;
    Game._pauseRects.settings = this.stamp(ctx, setX, setY, setW, 36, t('pause.settings'), { hover: this._hover(setX, setY, setW, 36), size: 15, icon: 'gear', color: c.ash });

    let btnY = H * 0.78, bh = 40;
    if (Game.reachedDawn) {
      const vW = 320, vX = W / 2 - vW / 2, vY = H * 0.75;
      Game._pauseRects.victory = this.stamp(ctx, vX, vY, vW, 40, t('pause.victory'), { prime: true, hover: this._hover(vX, vY, vW, 40), size: 17 });
      btnY = H * 0.84;
    }
    // продолжить / в меню — рядом
    const contW = 230, quitW = 190, contX = W / 2 - contW - 10, quitX = W / 2 + 10;
    Game._pauseRects.resume = this.stamp(ctx, contX, btnY, contW, bh, t('pause.resume'), { prime: true, hover: this._hover(contX, btnY, contW, bh), size: 17, icon: 'play' });
    Game._pauseRects.quit = this.stamp(ctx, quitX, btnY, quitW, bh, t('pause.quit'), { hover: this._hover(quitX, btnY, quitW, bh), size: 16, color: c.danger, icon: 'back' });
  },

  drawSettings(ctx) {
    const W = Game.viewW, H = Game.viewH;
    this.dim(ctx, 0.55);
    ctx.textAlign = 'center';

    // заголовок
    this.heatText(ctx, t('settings.title'), W / 2, H * 0.18, 36, 700, CONFIG.colors.hot, 'center', 'rgba(255,150,40,0.3)');

    Game._settingsRects = {};

    // --- ГРОМКОСТЬ ---
    const volLabelY = H * 0.30;
    this.heatText(ctx, t('settings.volume'), W / 2, volLabelY, 12, 300, CONFIG.colors.emberDim, 'center');
    ctx.textAlign = 'center';

    const bw = 240, bh = 10, bx = W / 2 - bw / 2, by = volLabelY + 16;
    // трек
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, 5); ctx.fill();
    // заполнение
    ctx.fillStyle = CONFIG.colors.player;
    ctx.beginPath(); ctx.roundRect(bx, by, bw * Audio2.volume, bh, 5); ctx.fill();
    // ручка
    const knobX = bx + bw * Audio2.volume;
    ctx.fillStyle = CONFIG.colors.player;
    ctx.beginPath(); ctx.arc(knobX, by + bh / 2, 7, 0, Math.PI * 2); ctx.fill();

    Game._settingsRects.volBar = { x: bx - 8, y: by - 8, w: bw + 16, h: bh + 16 };   // расширенная клик-зона
    Game._settingsRects.volTrack = { x: bx, w: bw };   // ИСТИННЫЕ границы трека для расчёта значения (0%/100% достижимы)

    // процент
    ctx.fillStyle = CONFIG.colors.text; ctx.font = '16px Consolas, monospace';
    ctx.fillText(Math.round(Audio2.volume * 100) + '%', W / 2, by + bh + 24);

    // кнопки −  +
    const btnSz = 28, btnGap = 20;
    const minX = bx - btnSz - btnGap, plusX = bx + bw + btnGap;
    const btnY2 = by - btnSz / 2 + bh / 2;
    const minHover = this._hover(minX, btnY2, btnSz, btnSz);
    const plusHover = this._hover(plusX, btnY2, btnSz, btnSz);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = minHover ? CONFIG.colors.player : 'rgba(255,255,255,0.3)';
    ctx.strokeRect(minX, btnY2, btnSz, btnSz);
    ctx.fillStyle = minHover ? CONFIG.colors.player : CONFIG.colors.text;
    ctx.font = '18px Consolas, monospace'; ctx.fillText('−', minX + btnSz / 2, btnY2 + 20);
    ctx.strokeStyle = plusHover ? CONFIG.colors.player : 'rgba(255,255,255,0.3)';
    ctx.strokeRect(plusX, btnY2, btnSz, btnSz);
    ctx.fillStyle = plusHover ? CONFIG.colors.player : CONFIG.colors.text;
    ctx.fillText('+', plusX + btnSz / 2, btnY2 + 20);
    Game._settingsRects.volMinus = { x: minX, y: btnY2, w: btnSz, h: btnSz };
    Game._settingsRects.volPlus  = { x: plusX, y: btnY2, w: btnSz, h: btnSz };

    // --- CRT ---
    const crtOn = Meta.data.crt !== false;
    const crtY = H * 0.50;
    this.heatText(ctx, t('settings.crt'), W / 2, crtY, 12, 300, CONFIG.colors.emberDim, 'center');
    ctx.textAlign = 'center';

    // toggle-переключатель
    const tgW = 56, tgH = 26, tgX = W / 2 - tgW / 2, tgY = crtY + 12;
    const tgHover = this._hover(tgX - 4, tgY - 4, tgW + 8, tgH + 8);
    ctx.fillStyle = crtOn ? CONFIG.colors.player + '55' : 'rgba(255,255,255,0.08)';
    ctx.beginPath(); ctx.roundRect(tgX, tgY, tgW, tgH, tgH / 2); ctx.fill();
    ctx.strokeStyle = crtOn ? CONFIG.colors.player : (tgHover ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)');
    ctx.lineWidth = 1.5; ctx.stroke();
    const knobCx = crtOn ? tgX + tgW - tgH / 2 : tgX + tgH / 2;
    ctx.fillStyle = crtOn ? CONFIG.colors.player : 'rgba(255,255,255,0.6)';
    ctx.beginPath(); ctx.arc(knobCx, tgY + tgH / 2, tgH / 2 - 3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = crtOn ? CONFIG.colors.player : CONFIG.colors.textDim;
    ctx.font = '13px Consolas, monospace';
    ctx.fillText(crtOn ? t('settings.on') : t('settings.off'), W / 2, tgY + tgH + 20);
    Game._settingsRects.crt = { x: tgX - 4, y: tgY - 4, w: tgW + 8, h: tgH + 8 };

    // --- ЯЗЫК ---
    this.drawLangRow(ctx, W / 2, H * 0.72, 'settings.language');

    // --- КНОПКА НАЗАД --- клеймо
    const backW = 200, backH = 40, backX = W / 2 - backW / 2, backY = H * 0.85;
    Game._settingsRects.back = this.stamp(ctx, backX, backY, backW, backH, t('settings.back'), { prime: true, hover: this._hover(backX, backY, backW, backH), size: 16, icon: 'back' });
  },

  _hover(x, y, w, h) {
    return Input.mouseX >= x && Input.mouseX <= x + w && Input.mouseY >= y && Input.mouseY <= y + h;
  },

  drawGameOver(ctx) {
    this.dim(ctx, 0.74);
    this.drawResults(ctx, t('results.dead'), CONFIG.colors.danger, Game.deathVoice);
  },

  drawWin(ctx) {
    this.dim(ctx, 0.74);
    // ДЕМО-ХУК: климакс демо (1-й босс повержен) → высокая нота + обещание полной версии
    if (Game.demoEnd) {
      this.drawResults(ctx, t('results.demoTitle'), CONFIG.colors.gold, Game.winVoice);
      this.heatText(ctx, t('results.demoSub'), Game.viewW / 2, Game.viewH * 0.26 + (Game.winVoice ? 78 : 56), 14, 300, CONFIG.colors.ember, 'center');
      return;
    }
    const finale = Game.depthIndex >= CONFIG.depths.length;   // покорено самое дно
    this.drawResults(ctx, finale ? t('results.finale') : t('results.survived'), finale ? CONFIG.colors.gold : CONFIG.colors.hot, Game.winVoice);
    // подзаголовок — ниже голоса (если он есть), чтобы строки не слипались
    this.heatText(ctx, finale ? t('results.finaleSub') : t('results.winSub'), Game.viewW / 2, Game.viewH * 0.26 + (Game.winVoice ? 78 : 56), 15, 300, CONFIG.colors.ash, 'center');
  },

  drawResults(ctx, title, color, voice) {
    const W = Game.viewW, H = Game.viewH, p = Game.player, c = CONFIG.colors;
    // имя забега — микро-нарратив над вердиктом
    if (Game.runName)
      this.heatText(ctx, '« ' + Game.runName + ' »', W / 2, H * 0.26 - 44, 17, 300, c.ash, 'center');
    // ВЕРДИКТ-клеймо (крупно, температура передаётся: кармин/бело-горяч/золото)
    this.heatText(ctx, title, W / 2, H * 0.26, 52, 700, color, 'center', 'rgba(255,120,40,0.35)');
    // голос Пепелища — emotional beat
    if (voice) {
      ctx.fillStyle = c.gold; ctx.font = this.f(20, 400, true); ctx.textAlign = 'center';
      ctx.globalAlpha = 0.92; ctx.fillText('« ' + voice + ' »', W / 2, H * 0.26 + 40); ctx.globalAlpha = 1;
    }

    // LEDGER — обугленная таблица (метка слева ash, число справа бело-горячее), выровнена
    const rows = [
      [t('results.time'), fmtTime(Game.time)], [t('results.level'), p.level],
      [t('results.kills'), p.kills], [t('results.bosses'), Game.bossKills],
      [t('results.score'), Game.score], [t('results.gold'), '+' + Game.runGold],
    ];
    let y = H * 0.42;
    for (const [k, v] of rows) {
      this.heatText(ctx, ('' + k).toUpperCase(), W / 2 - 16, y, 13, 300, c.ash, 'right');
      this.heatText(ctx, '' + v, W / 2 + 16, y, 19, 600, c.hot, 'left');
      y += 30;
    }
    // реликвии забега (кованые слоты)
    if (Game.relicsOwned && Game.relicsOwned.length) {
      this.heatText(ctx, t('results.relics'), W / 2, y + 2, 11, 300, c.emberDim, 'center');
      this.slotRow(ctx, W / 2, y + 10, Game.relicsOwned.map(key => CONFIG.relics[key]).filter(Boolean).map(rd => [rd.icon, rd.color, '✦', false]));
      y += 60;
    }
    // разблокировки — заметное событие (✦-клеймо)
    if (Game.newUnlocks && Game.newUnlocks.length) {
      const reveals: string[] = [], contentNames: string[] = [];
      for (const k of Game.newUnlocks) {
        const u = CONFIG.unlocks.find((x: any) => x.key === k);
        if (u && u.kind === 'reveal') { reveals.push(tHint(k, u.hint)); continue; }
        if (CONFIG.weapons[k]) contentNames.push(tName('weapons', k, CONFIG.weapons[k].name));
        else if (CONFIG.evolutions[k]) contentNames.push(tName('evolutions', k, CONFIG.evolutions[k].name));
        else if (CONFIG.characters[k]) contentNames.push(tName('characters', k, CONFIG.characters[k].name));
        else { const pd = CONFIG.paths.find((pp: any) => pp.key === k); contentNames.push(pd ? tName('paths', k, pd.name) : k); }
      }
      if (contentNames.length) { this.heatText(ctx, '✦ ' + t('results.unlocked', { names: contentNames.join(', ') }), W / 2, y + 8, 17, 600, c.xp, 'center'); y += 32; }
      for (const r of reveals) {
        ctx.globalAlpha = 0.7 + 0.3 * Math.sin(Game.clock * 3);
        this.heatText(ctx, '✦ ' + r, W / 2, y + 5, 16, 600, c.gold, 'center');
        ctx.globalAlpha = 1; y += 26;
      }
    }
    // кнопки — клеймо рестарт (prime) + меню (ghost)
    const rW = 240, rX = W / 2 - rW / 2, rsHover = this._hover(rX, y + 6, rW, 38);
    this.stamp(ctx, rX, y + 6, rW, 38, t('results.restart'), { prime: true, hover: rsHover || (Math.floor(Game.clock * 2) % 2) === 0, size: 17, icon: 'reroll' });
    const mW = 200, mX = W / 2 - mW / 2, mY = y + 6 + 38 + 10;
    Game._resultMenuRect = this.stamp(ctx, mX, mY, mW, 34, t('results.menu'), { hover: this._hover(mX, mY, mW, 34), size: 15, color: c.ash, icon: 'back' });
  },
};
